import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_VIDEO_PROVIDER, DEFAULT_VIDEO_MODEL, VIDEO_MODELS_CONFIG } from '../config/models';
import { createConsentAwareJSONStorage } from '../utils/consentAwareStorage';
import type { MotionPreset, VideoFrame } from '../types';

export interface GeneratedVideo {
  id: string;
  url: string;
  prompt: string;
  provider: string;
  model: string;
  quality: string;
  duration: string;
  ratio?: string;
  timestamp: number;
  poster?: string;
  thumbnailUrl?: string;
  spritesheetUrl?: string;
  startFrameUrl?: string;
  endFrameUrl?: string;
  referenceImageUrls?: Array<{ url: string; s3Key?: string }>; // Референсные изображения для Veo 3.1
  s3Key?: string; // Ключ в S3 для удаления
  motions?: any[]; // Motion presets для recreate
  generationOptions?: any; // Дополнительные опции генерации
  // Error card support
  status?: 'completed' | 'failed';
  errorMessage?: string;
  // Информация об авторе (для командного режима)
  author?: {
    firstName?: string;
    lastName?: string;
    username?: string;
    photoUrl?: string;
  };
}

export interface SelectedMotion {
  id: string;
  strength: number;
}

export interface ActiveGeneration {
  id: string;
  provider: string;
  model: string;
  timestamp: number;
  // Статус генерации
  status?: 'queue' | 'generating' | 'loading' | 'error';
  // Дополнительные параметры для точного расчета времени генерации
  quality?: string;
  duration?: string;
  ratio?: string;
  hasFrames?: boolean;
  prompt?: string; // Промпт генерации для отображения на карточке
  // Team mode - генерация другого участника
  isTeamMember?: boolean; // true если это генерация другого участника команды
  userId?: string;
  userName?: string;
  userPhoto?: string;
  folderId?: string;
}

interface VideoStoreState {
  // Настройки провайдера и модели
  currentProvider: string;
  currentModel: string;
  
  // Параметры видео
  ratio: string;
  quality: string;
  duration: string;
  
  // Сохраненные настройки для каждого провайдера (для восстановления при переключении)
  modelSettings: Record<string, {
    ratio?: string;
    quality?: string;
    duration?: string;
    enhanceMode?: boolean;
    seed?: number | null;
    audioEnabled?: boolean; // Для Veo 3/3.1
  }>;
  
  // Audio генерация (для Veo 3/3.1)
  audioEnabled: boolean;

  // Camera control (Kling models)
  cameraControl: {
    type: string;
    config?: { horizontal?: number; vertical?: number; pan?: number; tilt?: number; roll?: number; zoom?: number };
  } | null;

  // Keep original sound (Kling O1/O3 v2v edit mode)
  keepOriginalSound: boolean;

  // Загруженные кадры
  frames: VideoFrame[];
  
  // Режим генерации для Veo 3.1 и Kling O1 (standard/reference/video2video)
  generationMode: 'standard' | 'reference' | 'video2video';

  // Grok V2V sub-mode: 'edit' (modifies existing video) | 'extend' (continues from last frame)
  v2vMode: 'edit' | 'extend';
  
  // Референсные изображения для Veo 3.1 и Kling O1 (Veo 3.1: до 3, Kling O1: до 7) — Create tab
  referenceImages: VideoFrame[];

  // Референсные видео для Seedance 2.0 reference-to-video (до 3)
  referenceVideos: VideoFrame[];
  // Референсные аудио для Seedance 2.0 reference-to-video (до 3)
  referenceAudios: VideoFrame[];

  // Референсные изображения для Edit tab (Kling O1/O3 V2V) — отдельные от Create tab
  editReferenceImages: VideoFrame[];

  // Effective trim duration for Edit tab video pricing (seconds)
  editTrimDuration: number | null;
  // Trim start offset (seconds) — needed to trim video server-side before sending to API
  editTrimStart: number;
  // Frame trim range for Create tab (Chat Composer) video frames
  frameTrimStart: number;
  frameTrimEnd: number | null;

  // Входное видео для Kling O1 Video-to-Video режима
  inputVideo: VideoFrame | null;
  inputVideoDuration: number | null; // Длительность входного видео в секундах
  
  // Motion presets
  availableMotions: MotionPreset[];
  selectedMotions: SelectedMotion[];
  favoriteMotionIds: string[];
  
  // Сгенерированные видео
  videos: GeneratedVideo[];
  
  // Состояние генерации
  isGenerating: boolean;
  generatingCount: number;
  activeGenerations: number; // Количество активных генераций
  activeGenerationDetails: ActiveGeneration[]; // Детали активных генераций (модель, провайдер)
  maxConcurrentGenerations: number; // Максимум одновременных генераций
  
  // Промпт
  currentPrompt: string;
  enhanceMode: boolean;
  autoTranslate: boolean;
  recreatePrompt: string; // Промпт для recreate функции
  pendingRetryPrompt: string | null; // Промпт для автоматического retry ошибочных генераций (null = неактивно)
  pendingEditTab: boolean; // Флаг для переключения sidebar на вкладку "Редактирование"
  sidebarActiveTab: 'create' | 'edit'; // Активная вкладка sidebar (Создание / Редактирование)

  // Multi-Shot mode (Kling 3.0)
  multiShotEnabled: boolean;
  shots: Array<{ id: string; prompt: string; duration: string }>;
  activeShotId: string;
  
  // Selected Elements (Kling 3.0 - persistent user elements selected for current generation)
  selectedElements: Array<{ id: string; name: string; frontalImageUrl: string; referenceImages: Array<{ s3Key: string; s3Url: string }>; klingElementId?: number | null }>;
  
  // Seed
  seed: number | null;
  
  // Действия
  setProvider: (provider: string) => void;
  setModel: (model: string) => void;
  setRatio: (ratio: string) => void;
  setQuality: (quality: string) => void;
  setDuration: (duration: string) => void;
  
  addFrame: (frame: VideoFrame) => void;
  removeFrame: (frameId: string) => void;
  updateFrame: (frameId: string, patch: Partial<VideoFrame>) => void;
  swapFrames: () => void;
  clearFrames: (deleteFiles?: boolean) => Promise<void>;
  clearGenerationState: () => Promise<void>; // Очистка всех параметров генерации
  
  setGenerationMode: (mode: 'standard' | 'reference' | 'video2video') => void;
  setV2vMode: (mode: 'edit' | 'extend') => void;
  addReferenceImage: (image: VideoFrame) => void;
  removeReferenceImage: (imageId: string) => void;
  clearReferenceImages: (deleteFiles?: boolean) => Promise<void>;

  // Reference videos (Seedance 2.0)
  addReferenceVideo: (video: VideoFrame) => void;
  removeReferenceVideo: (videoId: string) => void;
  clearReferenceVideos: () => void;
  updateReferenceVideoTrim: (videoId: string, trimStart: number, trimEnd: number) => void;
  // Reference audios (Seedance 2.0)
  addReferenceAudio: (audio: VideoFrame) => void;
  removeReferenceAudio: (audioId: string) => void;
  clearReferenceAudios: () => void;
  updateReferenceAudioTrim: (audioId: string, trimStart: number, trimEnd: number) => void;

  // Edit tab reference images (Kling O1/O3 V2V) — отдельные от Create tab
  addEditReferenceImage: (image: VideoFrame) => void;
  removeEditReferenceImage: (imageId: string) => void;
  clearEditReferenceImages: (deleteFiles?: boolean) => Promise<void>;
  setEditTrimDuration: (duration: number | null) => void;
  setEditTrimStart: (start: number) => void;
  setFrameTrimRange: (start: number, end: number | null) => void;

  // Kling O1 Video-to-Video methods
  setInputVideo: (video: VideoFrame | null) => void;
  removeInputVideo: (deleteFile?: boolean) => Promise<void>;
  setInputVideoDuration: (duration: number | null) => void;
  
  setAvailableMotions: (motions: MotionPreset[]) => void;
  addSelectedMotion: (motionId: string, strength: number) => void;
  removeSelectedMotion: (motionId: string) => void;
  updateMotionStrength: (motionId: string, strength: number) => void;
  clearSelectedMotions: () => void;
  toggleFavoriteMotion: (motionId: string) => void;
  
  addVideo: (video: GeneratedVideo) => void;
  removeVideo: (videoId: string) => void;
  clearVideos: () => void;
  setVideos: (videos: GeneratedVideo[]) => void;
  
  setCurrentPrompt: (prompt: string) => void;
  toggleEnhanceMode: () => void;
  setAutoTranslate: (autoTranslate: boolean) => void;
  setSeed: (seed: number | null) => void;
  setAudioEnabled: (enabled: boolean) => void;
  setCameraControl: (control: { type: string; config?: { horizontal?: number; vertical?: number; pan?: number; tilt?: number; roll?: number; zoom?: number } } | null) => void;
  setKeepOriginalSound: (keep: boolean) => void;

  // Multi-Shot actions (Kling 3.0)
  setMultiShotEnabled: (enabled: boolean) => void;
  setActiveShotId: (id: string) => void;
  addShot: () => void;
  removeShot: (shotId: string) => void;
  updateShotPrompt: (shotId: string, prompt: string) => void;
  updateShotDuration: (shotId: string, duration: string) => void;
  resetShots: () => void;
  setShots: (shots: Array<{ id: string; prompt: string; duration: string }>) => void;
  
  // Selected Elements actions (Kling 3.0 - from persistent library)
  selectElement: (element: { id: string; name: string; frontalImageUrl: string; referenceImages: Array<{ s3Key: string; s3Url: string }>; klingElementId?: number | null }) => void;
  deselectElement: (elementId: string) => void;
  setSelectedElements: (elements: Array<{ id: string; name: string; frontalImageUrl: string; referenceImages: Array<{ s3Key: string; s3Url: string }>; klingElementId?: number | null }>) => void;
  clearSelectedElements: () => void;
  
  setGenerating: (isGenerating: boolean, count?: number) => void;
  incrementActiveGenerations: (provider: string, model: string, details?: Partial<ActiveGeneration>) => void;
  decrementActiveGenerations: (generationId?: string) => void;
  updateGenerationStatus: (generationId: string, status: 'queue' | 'generating' | 'loading' | 'error') => void;
  setMaxConcurrentGenerations: (max: number) => void;
  
  // Recreate functionality - устанавливает промт для повторного создания
  setRecreatePrompt: (prompt: string) => void;
  setPendingRetryPrompt: (prompt: string | null) => void;
  setPendingEditTab: (pending: boolean) => void;
  setSidebarActiveTab: (tab: 'create' | 'edit') => void;
  loadFrameFromUrl: (url: string, type: 'start' | 'end' | 'actTwoCharacter' | 'referenceVideo') => Promise<void>;
  loadReferenceImageFromUrl: (url: string, s3Key?: string) => Promise<void>;
  loadInputVideoFromUrl: (url: string, duration?: number) => Promise<void>;
  
  // Team mode: триггер для принудительного обновления при получении SSE событий
  teamRefreshTrigger: number;
  triggerTeamRefresh: () => void;
  
  // Pagination state
  hasMoreVideos: boolean;
  currentOffset: number;
  isLoadingMore: boolean;
  
  // SWR Cache state
  lastLoadedAt: number | null;
  cacheKey: string | null;
  isRevalidating: boolean;
  
  // Pagination actions
  appendVideos: (videos: GeneratedVideo[]) => void;
  setHasMoreVideos: (hasMore: boolean) => void;
  setCurrentOffset: (offset: number) => void;
  setIsLoadingMore: (isLoading: boolean) => void;
  resetPagination: () => void;
  
  // SWR Cache actions
  setLastLoadedAt: (timestamp: number | null) => void;
  setCacheKey: (key: string | null) => void;
  setIsRevalidating: (isRevalidating: boolean) => void;
  invalidateCache: () => void;
  
}

// Флаг для пропуска синхронизации с БД при регенерации из галереи
let _skipFramesSync = false;
export const setSkipFramesSync = (skip: boolean) => { _skipFramesSync = skip; };
export const getSkipFramesSync = () => _skipFramesSync;

const useVideoStore = create<VideoStoreState>()(
  persist(
    (set, get) => ({
      // Начальное состояние
      currentProvider: DEFAULT_VIDEO_PROVIDER,
      currentModel: DEFAULT_VIDEO_MODEL,
      
      ratio: '16:9',
      quality: '1080p',
      duration: '10s',
      
      modelSettings: {},
      
      // Audio генерация (для Veo 3/3.1) - по умолчанию включено
      audioEnabled: true,

      // Camera control (Kling) - по умолчанию выключено
      cameraControl: null,

      // Keep original sound (Kling v2v) - по умолчанию включено
      keepOriginalSound: true,

      frames: [],
      generationMode: 'standard',
      v2vMode: 'edit',
      referenceImages: [],
      referenceVideos: [],
      referenceAudios: [],
      editReferenceImages: [],
      editTrimDuration: null,
      editTrimStart: 0,
      frameTrimStart: 0,
      frameTrimEnd: null,
      inputVideo: null,
      inputVideoDuration: null,
      availableMotions: [],
      selectedMotions: [],
      favoriteMotionIds: [],
      videos: [],
      
      isGenerating: false,
      generatingCount: 0,
      activeGenerations: 0,
      activeGenerationDetails: [],
      maxConcurrentGenerations: 1, // Дефолт для free tier
      
      currentPrompt: '',
      enhanceMode: false,
      autoTranslate: true,
      recreatePrompt: '',
      pendingRetryPrompt: null,
      pendingEditTab: false,
      sidebarActiveTab: 'create',
      seed: null,

      // Multi-Shot mode (Kling 3.0)
      multiShotEnabled: false,
      activeShotId: 'shot-1',
      shots: [
        { id: 'shot-1', prompt: '', duration: '3' },
        { id: 'shot-2', prompt: '', duration: '3' }
      ],
      
      // Selected Elements (Kling 3.0)
      selectedElements: [],
      
      // Team mode: триггер для принудительного обновления
      teamRefreshTrigger: 0,
      
      // Pagination state
      hasMoreVideos: true,
      currentOffset: 0,
      isLoadingMore: false,
      
      // SWR Cache state - для оптимизации загрузки при переключении вкладок
      lastLoadedAt: null as number | null,
      cacheKey: null as string | null,
      isRevalidating: false,
      
      // Реализация действий
      setProvider: (provider) => set((state) => {
        const updates: Partial<VideoStoreState> = { currentProvider: provider };
        
        // При переключении провайдера восстанавливаем сохраненные настройки для этого провайдера
        const savedSettings = state.modelSettings[provider];
        if (savedSettings) {
          if (savedSettings.ratio) updates.ratio = savedSettings.ratio;
          if (savedSettings.quality) updates.quality = savedSettings.quality;
          if (savedSettings.duration) updates.duration = savedSettings.duration;
          if (savedSettings.enhanceMode !== undefined) updates.enhanceMode = savedSettings.enhanceMode;
          if (savedSettings.seed !== undefined) updates.seed = savedSettings.seed;
          if (savedSettings.audioEnabled !== undefined) updates.audioEnabled = savedSettings.audioEnabled;
        }
        
        // Для Veo, Kling и Seedance провайдера по умолчанию включаем аудио если нет сохраненных настроек
        // (Veo 3/3.1, Kling 2.6 и Seedance 1.5 Pro поддерживают генерацию аудио)
        if ((provider === 'veo' || provider === 'kling' || provider === 'seedance') && savedSettings?.audioEnabled === undefined) {
          updates.audioEnabled = true;
        }
        
        // Motion presets поддерживаются только в Higgsfield
        if (provider !== 'higgsfield' && state.selectedMotions.length > 0) {
          updates.selectedMotions = [];
        }
        
        // Reference mode: Veo 3.1 Elements (always reference) and Kling O1
        // Video2video mode поддерживается только в Kling O1
        const isVeo31Elements = provider === 'veo' && state.currentModel === 'veo-3.1-elements';
        const isKlingO1 = provider === 'kling' && state.currentModel === 'kling-o1';
        if (!isVeo31Elements && !isKlingO1 && state.generationMode === 'reference') {
          updates.generationMode = 'standard';
        }
        // Video2video для Kling O1 / O3
        const isKlingO1OrO3 = provider === 'kling' && (state.currentModel === 'kling-o1' || state.currentModel === 'kling-o3');
        if (!isKlingO1OrO3 && state.generationMode === 'video2video') {
          updates.generationMode = 'standard';
        }

        // Очищаем inputVideo при переключении с Kling на другой провайдер
        // (inputVideo используется только в video2video режиме Kling O1/O3)
        if (state.currentProvider === 'kling' && provider !== 'kling' && state.inputVideo) {
          updates.inputVideo = null;
          updates.inputVideoDuration = null;
        }

        // Camera control — сбрасываем при переключении с Kling
        if (provider !== 'kling' && state.cameraControl) {
          updates.cameraControl = null;
        }

        return updates;
      }),
      setModel: (model) => set((state) => {
        const updates: Partial<VideoStoreState> = { currentModel: model };
        
        // При переключении модели очищаем параметры, которые могут не поддерживаться
        // (дополнительная проверка на уровне модели)
        
        // Motion presets поддерживаются только в Higgsfield
        if (state.currentProvider !== 'higgsfield' && state.selectedMotions.length > 0) {
          updates.selectedMotions = [];
        }
        
        // Reference mode: Veo 3.1 Elements (always reference) and Kling O1
        // Video2video mode поддерживается для Kling O1 / O3
        const isVeo31Elements = state.currentProvider === 'veo' && model === 'veo-3.1-elements';
        const isKlingO1 = state.currentProvider === 'kling' && model === 'kling-o1';
        const isKlingO3 = state.currentProvider === 'kling' && model === 'kling-o3';
        const isKlingO1OrO3 = isKlingO1 || isKlingO3;
        // Auto-set reference mode for Elements model
        if (isVeo31Elements && state.generationMode !== 'reference') {
          updates.generationMode = 'reference';
          updates.ratio = '16:9';
        }
        if (!isVeo31Elements && !isKlingO1OrO3 && state.generationMode === 'reference') {
          updates.generationMode = 'standard';
        }
        // Video2video для Kling O1 / O3
        if (!isKlingO1OrO3 && state.generationMode === 'video2video') {
          updates.generationMode = 'standard';
        }
        
        // Очищаем inputVideo при переключении с Kling O1/O3 на другую модель
        const wasKlingO1OrO3 = state.currentProvider === 'kling' && (state.currentModel === 'kling-o1' || state.currentModel === 'kling-o3');
        if (wasKlingO1OrO3 && !isKlingO1OrO3 && state.inputVideo) {
          updates.inputVideo = null;
          updates.inputVideoDuration = null;
        }
        
        // Camera control — сбрасываем при переключении с Kling
        if (state.currentProvider !== 'kling' && state.cameraControl) {
          updates.cameraControl = null;
        }

        // Очищаем качество, если новая модель его не поддерживает
        const providerConfig = VIDEO_MODELS_CONFIG[state.currentProvider];
        const modelConfig = providerConfig?.models.find((m: any) => m.id === model);
        const modelParams = modelConfig?.parameters;
        
        if (modelParams && Array.isArray(modelParams.quality) && modelParams.quality.length === 0) {
          // Модель не поддерживает выбор качества - очищаем его
          updates.quality = '';
        } else if (!modelParams && state.quality) {
          // Если конфиг модели не найден, пытаемся быть осторожными - очищаем непредвиденные параметры
          // (это может произойти для новых моделей, которые еще не в конфиге)
          // Оставляем качество, как было - это лучше, чем терять данные
        }
        
        return updates;
      }),
      setRatio: (ratio) => set((state) => ({
        ratio,
        modelSettings: {
          ...state.modelSettings,
          [state.currentProvider]: {
            ...state.modelSettings[state.currentProvider],
            ratio
          }
        }
      })),
      setQuality: (quality) => set((state) => ({
        quality,
        modelSettings: {
          ...state.modelSettings,
          [state.currentProvider]: {
            ...state.modelSettings[state.currentProvider],
            quality
          }
        }
      })),
      setDuration: (duration) => set((state) => ({
        duration,
        modelSettings: {
          ...state.modelSettings,
          [state.currentProvider]: {
            ...state.modelSettings[state.currentProvider],
            duration
          }
        }
      })),
      
      addFrame: (frame) => set((state) => {
        // Если кадр с таким типом уже существует - заменяем его
        const existingIndex = state.frames.findIndex(f => f.type === frame.type);
        if (existingIndex !== -1) {
          const newFrames = [...state.frames];
          newFrames[existingIndex] = frame;
          return { frames: newFrames };
        }
        // Иначе добавляем новый
        return { frames: [...state.frames, frame] };
      }),
      
      removeFrame: (frameId) => set((state) => ({
        frames: state.frames.filter(f => f.id !== frameId)
      })),

      updateFrame: (frameId, patch) => set((state) => ({
        frames: state.frames.map(f => f.id === frameId ? { ...f, ...patch } : f)
      })),
      
      swapFrames: () => set((state) => {
        const startFrame = state.frames.find(f => f.type === 'start');
        const endFrame = state.frames.find(f => f.type === 'end');
        
        if (!startFrame || !endFrame) return state;
        
        // Меняем типы местами для обоих кадров за одну операцию
        const newFrames = state.frames.map(frame => {
          if (frame.id === startFrame.id) {
            return { ...frame, type: 'end' as const };
          }
          if (frame.id === endFrame.id) {
            return { ...frame, type: 'start' as const };
          }
          return frame;
        });
        
        return { frames: newFrames };
      }),
      
      clearFrames: async (deleteFiles = true) => {
        const { frames } = useVideoStore.getState();
        
        // Если deleteFiles = false, просто очищаем UI (для синхронизации с БД)
        if (!deleteFiles) {
          set({ frames: [] });
          return;
        }
        
        if (frames.length === 0) {
          set({ frames: [] });
          return;
        }
        
        // Импортируем apiClient динамически
        const { apiClient } = await import('../services/api');
        
        // Удаляем все кадры из БД и S3
        const deletePromises = frames.map(async (frame) => {
          // Удаляем из БД если есть dbId
          if (frame.dbId) {
            try {
              await apiClient.deleteVideoComposerAttachment(frame.dbId);
            } catch (error) {
              // Failed to delete frame from DB
            }
          }
          
          // Удаляем из S3 (не удаляем временные temp- файлы и файлы из галереи)
          const shouldDeleteFromS3 = frame.s3Key && !frame.s3Key.startsWith('temp-') && !frame.fromGallery;
          
          if (shouldDeleteFromS3) {
            try {
              await apiClient.deleteFromS3([frame.s3Key]);
            } catch (error) {
              // Failed to delete frame from S3
            }
          }
        });
        
        // Ждем завершения всех удалений
        await Promise.all(deletePromises);
        
        // Очищаем UI
        set({ frames: [] });
      },
      
      // Очистка всех параметров генерации после успешной генерации
      clearGenerationState: async () => {
        const { frames, referenceImages, editReferenceImages } = useVideoStore.getState();
        
        // Импортируем apiClient динамически
        const { apiClient } = await import('../services/api');
        
        // Удаляем все кадры и референсные изображения из БД и S3
        const allFrames = [...frames, ...referenceImages, ...editReferenceImages];
        const deletePromises = allFrames.map(async (frame) => {
          // Удаляем из БД если есть dbId
          if (frame.dbId) {
            try {
              await apiClient.deleteVideoComposerAttachment(frame.dbId);
            } catch (error) {
              // Failed to delete attachment from DB
            }
          }
          
          // Удаляем из S3 (не удаляем временные temp- файлы и файлы из галереи)
          const shouldDeleteFromS3 = frame.s3Key && !frame.s3Key.startsWith('temp-') && !frame.fromGallery;
          
          if (shouldDeleteFromS3) {
            try {
              await apiClient.deleteFromS3([frame.s3Key]);
            } catch (error) {
              // Failed to delete attachment from S3
            }
          }
        });
        
        // Ждем завершения всех удалений
        await Promise.all(deletePromises);
        
        // Очищаем все параметры генерации
        set({ 
          frames: [],
          referenceImages: [],
          referenceVideos: [],
          referenceAudios: [],
          editReferenceImages: [],
          editTrimDuration: null,
          editTrimStart: 0,
          frameTrimStart: 0,
          frameTrimEnd: null,
          selectedMotions: [],
          seed: null,
          recreatePrompt: '',
          pendingRetryPrompt: null,
          pendingEditTab: false,
          sidebarActiveTab: 'create',
          multiShotEnabled: false,
          shots: [
            { id: 'shot-1', prompt: '', duration: '3' },
            { id: 'shot-2', prompt: '', duration: '3' }
          ],
          selectedElements: []
        });
      },
      
      setV2vMode: (mode) => set({ v2vMode: mode }),

      setGenerationMode: (mode) => set((state) => {
        // При переключении в reference mode для Veo 3.1 блокируем ratio на 16:9
        // Для Kling O1 ratio НЕ блокируется (поддерживает все 3 формата в reference mode)
        const updates: Partial<VideoStoreState> = { generationMode: mode };
        const isKlingO1 = state.currentProvider === 'kling' && state.currentModel === 'kling-o1';
        if (mode === 'reference' && state.ratio !== '16:9' && !isKlingO1) {
          updates.ratio = '16:9';
        }
        return updates;
      }),
      
      addReferenceImage: (image) => set((state) => {
        // Если изображение с таким id уже существует - заменяем его (обновление прогресса)
        const existingIndex = state.referenceImages.findIndex(img => img.id === image.id);
        if (existingIndex !== -1) {
          const newReferenceImages = [...state.referenceImages];
          newReferenceImages[existingIndex] = image;
          return { referenceImages: newReferenceImages };
        }
        
        // Максимум 7 референсных изображений (Veo 3.1: 3, Kling O1: 7)
        // Конкретная проверка лимита делается в useVideoFileHandling.ts
        if (state.referenceImages.length >= 7) {
          return state;
        }
        
        // Иначе добавляем новое
        return { referenceImages: [...state.referenceImages, image] };
      }),
      
      removeReferenceImage: (imageId) => set((state) => ({
        referenceImages: state.referenceImages.filter(img => img.id !== imageId)
      })),
      
      clearReferenceImages: async (deleteFiles = true) => {
        const { referenceImages } = useVideoStore.getState();
        
        // Если deleteFiles = false, просто очищаем UI (для синхронизации с БД)
        if (!deleteFiles) {
          set({ referenceImages: [] });
          return;
        }
        
        if (referenceImages.length === 0) {
          set({ referenceImages: [] });
          return;
        }
        
        console.log(`🗑️ Clearing ${referenceImages.length} reference images from S3 and DB...`);
        
        // Импортируем apiClient динамически
        const { apiClient } = await import('../services/api');
        
        // Удаляем все референсные изображения из БД и S3
        const deletePromises = referenceImages.map(async (refImage) => {
          // Удаляем из БД если есть dbId
          if (refImage.dbId) {
            try {
              await apiClient.deleteVideoComposerAttachment(refImage.dbId);
            } catch (error) {
              // Failed to delete reference image from DB
            }
          }
          
          // Удаляем из S3 (не удаляем временные temp- файлы и файлы из галереи)
          const shouldDeleteFromS3 = refImage.s3Key && !refImage.s3Key.startsWith('temp-') && !refImage.fromGallery;
          
          if (shouldDeleteFromS3) {
            try {
              await apiClient.deleteFromS3([refImage.s3Key]);
            } catch (error) {
              // Failed to delete reference image from S3
            }
          }
        });
        
        // Ждем завершения всех удалений
        await Promise.all(deletePromises);
        
        // Очищаем UI
        set({ referenceImages: [] });
      },

      // Reference videos (Seedance 2.0 r2v, max 3)
      addReferenceVideo: (video) => set((state) => {
        const existingIndex = state.referenceVideos.findIndex(v => v.id === video.id);
        if (existingIndex !== -1) {
          const updated = [...state.referenceVideos];
          updated[existingIndex] = video;
          return { referenceVideos: updated };
        }
        if (state.referenceVideos.length >= 3) return state;
        return { referenceVideos: [...state.referenceVideos, video] };
      }),
      removeReferenceVideo: (videoId) => set((state) => ({
        referenceVideos: state.referenceVideos.filter(v => v.id !== videoId)
      })),
      clearReferenceVideos: () => set({ referenceVideos: [] }),
      updateReferenceVideoTrim: (videoId, trimStart, trimEnd) => set((state) => ({
        referenceVideos: state.referenceVideos.map(v =>
          v.id === videoId ? { ...v, trimStart, trimEnd } : v
        ),
      })),

      // Reference audios (Seedance 2.0 r2v, max 3)
      addReferenceAudio: (audio) => set((state) => {
        const existingIndex = state.referenceAudios.findIndex(a => a.id === audio.id);
        if (existingIndex !== -1) {
          const updated = [...state.referenceAudios];
          updated[existingIndex] = audio;
          return { referenceAudios: updated };
        }
        if (state.referenceAudios.length >= 3) return state;
        return { referenceAudios: [...state.referenceAudios, audio] };
      }),
      removeReferenceAudio: (audioId) => set((state) => ({
        referenceAudios: state.referenceAudios.filter(a => a.id !== audioId)
      })),
      clearReferenceAudios: () => set({ referenceAudios: [] }),
      updateReferenceAudioTrim: (audioId, trimStart, trimEnd) => set((state) => ({
        referenceAudios: state.referenceAudios.map(a =>
          a.id === audioId ? { ...a, trimStart, trimEnd } : a
        ),
      })),

      // Edit tab reference images (Kling O1/O3 V2V) — отдельные от Create tab
      addEditReferenceImage: (image) => set((state) => {
        const existingIndex = state.editReferenceImages.findIndex(img => img.id === image.id);
        if (existingIndex !== -1) {
          const newImages = [...state.editReferenceImages];
          newImages[existingIndex] = image;
          return { editReferenceImages: newImages };
        }
        if (state.editReferenceImages.length >= 7) return state;
        return { editReferenceImages: [...state.editReferenceImages, image] };
      }),

      removeEditReferenceImage: (imageId) => set((state) => ({
        editReferenceImages: state.editReferenceImages.filter(img => img.id !== imageId)
      })),

      clearEditReferenceImages: async (deleteFiles = true) => {
        const { editReferenceImages } = useVideoStore.getState();
        if (!deleteFiles) { set({ editReferenceImages: [] }); return; }
        if (editReferenceImages.length === 0) { set({ editReferenceImages: [] }); return; }
        const { apiClient } = await import('../services/api');
        const deletePromises = editReferenceImages.map(async (refImage) => {
          if (refImage.dbId) {
            try { await apiClient.deleteVideoComposerAttachment(refImage.dbId); } catch { /* ignore */ }
          }
          const shouldDeleteFromS3 = refImage.s3Key && !refImage.s3Key.startsWith('temp-') && !refImage.fromGallery;
          if (shouldDeleteFromS3) {
            try { await apiClient.deleteFromS3([refImage.s3Key]); } catch { /* ignore */ }
          }
        });
        await Promise.all(deletePromises);
        set({ editReferenceImages: [] });
      },

      setEditTrimDuration: (duration) => set({ editTrimDuration: duration }),
      setEditTrimStart: (start) => set({ editTrimStart: start }),
      setFrameTrimRange: (start, end) => set({ frameTrimStart: start, frameTrimEnd: end }),

      // Kling O1 Video-to-Video methods
      setInputVideo: (video) => set({ inputVideo: video }),
      
      removeInputVideo: async (deleteFile = true) => {
        const { inputVideo } = useVideoStore.getState();
        
        if (!inputVideo) {
          set({ inputVideo: null, inputVideoDuration: null });
          return;
        }
        
        // Если deleteFile = false, просто очищаем UI
        if (!deleteFile) {
          set({ inputVideo: null, inputVideoDuration: null });
          return;
        }
        
        console.log(`🗑️ Clearing input video from S3 and DB...`);
        
        // Импортируем apiClient динамически
        const { apiClient } = await import('../services/api');
        
        // Удаляем из БД если есть dbId
        if (inputVideo.dbId) {
          try {
            await apiClient.deleteVideoComposerAttachment(inputVideo.dbId);
          } catch (error) {
            console.error('Failed to delete input video from DB:', error);
          }
        }
        
        // Удаляем из S3 (не удаляем временные temp- файлы и файлы из галереи)
        const shouldDeleteFromS3 = inputVideo.s3Key && !inputVideo.s3Key.startsWith('temp-') && !inputVideo.fromGallery;
        
        if (shouldDeleteFromS3) {
          try {
            await apiClient.deleteFromS3([inputVideo.s3Key]);
          } catch (error) {
            console.error('Failed to delete input video from S3:', error);
          }
        }
        
        // Очищаем UI
        set({ inputVideo: null, inputVideoDuration: null });
      },
      
      setInputVideoDuration: (duration) => set({ inputVideoDuration: duration }),
      
      setAvailableMotions: (motions) => set({ availableMotions: motions }),
      
      addSelectedMotion: (motionId, strength) => set((state) => {
        // Проверяем, не выбран ли уже этот motion
        if (state.selectedMotions.find(m => m.id === motionId)) {
          return state;
        }
        // Higgsfield поддерживает максимум 2 motion
        if (state.selectedMotions.length >= 2) {
          return state;
        }
        return {
          selectedMotions: [...state.selectedMotions, { id: motionId, strength }]
        };
      }),
      
      removeSelectedMotion: (motionId) => set((state) => ({
        selectedMotions: state.selectedMotions.filter(m => m.id !== motionId)
      })),
      
      updateMotionStrength: (motionId, strength) => set((state) => ({
        selectedMotions: state.selectedMotions.map(m =>
          m.id === motionId ? { ...m, strength } : m
        )
      })),
      
      clearSelectedMotions: () => set({ selectedMotions: [] }),
      
      toggleFavoriteMotion: (motionId) => set((state) => {
        const isFavorite = state.favoriteMotionIds.includes(motionId);
        if (isFavorite) {
          return {
            favoriteMotionIds: state.favoriteMotionIds.filter(id => id !== motionId)
          };
        } else {
          return {
            favoriteMotionIds: [...state.favoriteMotionIds, motionId]
          };
        }
      }),
      
      addVideo: (video) => set((state) => {
        // Проверяем на дубликат по ID
        if (state.videos.some(v => v.id === video.id)) {
          return state; // Не добавляем дубликат
        }
        return {
          videos: [video, ...state.videos]
        };
      }),
      
      removeVideo: (videoId) => set((state) => ({
        videos: state.videos.filter(v => v.id !== videoId)
      })),
      
      clearVideos: () => set({ videos: [] }),
      
      setVideos: (videos) => set({ videos }),
      
      // Pagination actions
      appendVideos: (videos) => set((state) => {
        // Фильтруем дубликаты по ID
        const existingIds = new Set(state.videos.map(v => v.id));
        const uniqueNewVideos = videos.filter(v => !existingIds.has(v.id));
        return {
          videos: [...state.videos, ...uniqueNewVideos]
        };
      }),
      
      setHasMoreVideos: (hasMore) => set({ hasMoreVideos: hasMore }),
      
      setCurrentOffset: (offset) => set({ currentOffset: offset }),
      
      setIsLoadingMore: (isLoading) => set({ isLoadingMore: isLoading }),
      
      resetPagination: () => set({ hasMoreVideos: true, currentOffset: 0, isLoadingMore: false }),
      
      // SWR Cache actions
      setLastLoadedAt: (timestamp: number | null) => set({ lastLoadedAt: timestamp }),
      
      setCacheKey: (key: string | null) => set({ cacheKey: key }),
      
      setIsRevalidating: (isRevalidating: boolean) => set({ isRevalidating }),
      
      // Инвалидация кэша (например, после генерации нового видео)
      invalidateCache: () => set({ lastLoadedAt: null }),
      
      setCurrentPrompt: (prompt) => set((state) => {
        // Auto-sync: remove elements whose @name is no longer in the prompt
        const filtered = state.selectedElements.filter(el => {
          const escaped = el.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          return new RegExp(`@${escaped}`, 'i').test(prompt);
        });
        return {
          currentPrompt: prompt,
          selectedElements: filtered.length !== state.selectedElements.length ? filtered : state.selectedElements,
        };
      }),
      setAutoTranslate: (autoTranslate) => set({ autoTranslate }),
      toggleEnhanceMode: () => set((state) => {
        const newEnhanceMode = !state.enhanceMode;
        return {
          enhanceMode: newEnhanceMode,
          modelSettings: {
            ...state.modelSettings,
            [state.currentProvider]: {
              ...state.modelSettings[state.currentProvider],
              enhanceMode: newEnhanceMode
            }
          }
        };
      }),
      setSeed: (seed) => set((state) => ({
        seed,
        modelSettings: {
          ...state.modelSettings,
          [state.currentProvider]: {
            ...state.modelSettings[state.currentProvider],
            seed
          }
        }
      })),
      
      setAudioEnabled: (enabled) => set((state) => ({
        audioEnabled: enabled,
        modelSettings: {
          ...state.modelSettings,
          [state.currentProvider]: {
            ...state.modelSettings[state.currentProvider],
            audioEnabled: enabled
          }
        }
      })),

      setCameraControl: (control) => set({ cameraControl: control }),
      setKeepOriginalSound: (keep) => set({ keepOriginalSound: keep }),
      
      // Multi-Shot actions (Kling 3.0)
      setMultiShotEnabled: (enabled) => set({ multiShotEnabled: enabled }),
      setActiveShotId: (id) => set({ activeShotId: id }),
      
      addShot: () => set((state) => {
        if (state.shots.length >= 5) return state;
        const newId = `shot-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`;
        return {
          shots: [...state.shots, { id: newId, prompt: '', duration: '3' }]
        };
      }),
      
      removeShot: (shotId) => set((state) => {
        if (state.shots.length <= 2) return state;
        return {
          shots: state.shots.filter(s => s.id !== shotId)
        };
      }),
      
      updateShotPrompt: (shotId, prompt) => set((state) => ({
        shots: state.shots.map(s => s.id === shotId ? { ...s, prompt } : s)
      })),
      
      updateShotDuration: (shotId, duration) => set((state) => ({
        shots: state.shots.map(s => s.id === shotId ? { ...s, duration } : s)
      })),
      
      resetShots: () => set({
        shots: [
          { id: 'shot-1', prompt: '', duration: '3' },
          { id: 'shot-2', prompt: '', duration: '3' }
        ],
        activeShotId: 'shot-1',
        multiShotEnabled: false
      }),

      setShots: (shots) => set({ shots }),
      
      // Selected Elements actions (Kling 3.0 - from persistent library)
      // Actual model-specific limits enforced at UI level (ElementsTooltip maxElements prop)
      // Store uses max possible (7 for O1/O3 without video ref)
      selectElement: (element) => set((state) => {
        if (state.selectedElements.length >= 7) return state;
        // Don't add duplicates
        if (state.selectedElements.some(el => el.id === element.id)) return state;
        return { selectedElements: [...state.selectedElements, element] };
      }),
      
      deselectElement: (elementId) => set((state) => ({
        selectedElements: state.selectedElements.filter(el => el.id !== elementId)
      })),

      setSelectedElements: (elements) => set({ selectedElements: elements }),
      
      clearSelectedElements: () => set({ selectedElements: [] }),
      
      setGenerating: (isGenerating, count = 1) => set({ 
        isGenerating, 
        generatingCount: count 
      }),
      
      incrementActiveGenerations: (provider, model, details) => set((state) => {
        // Если переданы детали (например, при восстановлении из API) - используем их
        // Иначе создаём новую генерацию из текущего состояния
        const newGeneration: ActiveGeneration = details ? {
          id: details.id || `gen-${Date.now()}-${Math.random()}`,
          provider: details.provider || provider,
          model: details.model || model,
          timestamp: details.timestamp || Date.now(),
          status: details.status || 'queue', // Начинаем с очереди
          quality: details.quality || state.quality,
          duration: details.duration || state.duration,
          ratio: details.ratio || state.ratio,
          hasFrames: details.hasFrames ?? state.frames.length > 0,
          prompt: details.prompt || state.currentPrompt, // Промпт для отображения на карточке
          // TEAM MODE: сохраняем информацию о пользователе
          isTeamMember: details.isTeamMember,
          userId: details.userId,
          userName: details.userName,
          userPhoto: details.userPhoto,
          folderId: details.folderId
        } : {
          id: `gen-${Date.now()}-${Math.random()}`,
          provider,
          model,
          timestamp: Date.now(),
          status: 'queue', // Начинаем с очереди
          // Сохраняем параметры генерации для точного расчета времени
          quality: state.quality,
          duration: state.duration,
          ratio: state.ratio,
          hasFrames: state.frames.length > 0,
          prompt: state.currentPrompt
        };
        const newDetails = [...state.activeGenerationDetails, newGeneration];
        // Для team member генераций не увеличиваем счётчик (они не занимают слот текущего пользователя)
        const newCount = details?.isTeamMember ? state.activeGenerations : state.activeGenerations + 1;
        return {
          activeGenerations: newCount,
          activeGenerationDetails: newDetails,
          isGenerating: newCount > 0 || newDetails.length > 0
        };
      }),
      
      decrementActiveGenerations: (generationId?: string) => set((state) => {
        let newDetails: ActiveGeneration[];
        let wasTeamMember = false;
        
        if (generationId) {
          // Удаляем по конкретному ID
          const genToRemove = state.activeGenerationDetails.find(g => g.id === generationId);
          wasTeamMember = genToRemove?.isTeamMember || false;
          newDetails = state.activeGenerationDetails.filter(gen => gen.id !== generationId);
        } else {
          // Старая логика: удаляем самую старую свою генерацию
          let removed = false;
          newDetails = state.activeGenerationDetails.filter(gen => {
            if (gen.isTeamMember) return true;
            if (!removed) {
              removed = true;
              return false;
            }
            return true;
          });
        }
        
        // Для team member генераций не уменьшаем счётчик
        const newCount = wasTeamMember ? state.activeGenerations : Math.max(0, state.activeGenerations - 1);
        return {
          activeGenerations: newCount,
          activeGenerationDetails: newDetails,
          isGenerating: newCount > 0 || newDetails.length > 0
        };
      }),
      
      // Обновление статуса генерации
      updateGenerationStatus: (generationId, status) => set((state) => ({
        activeGenerationDetails: state.activeGenerationDetails.map(gen =>
          gen.id === generationId ? { ...gen, status } : gen
        )
      })),
      
      setMaxConcurrentGenerations: (max) => set({ maxConcurrentGenerations: max }),
      
      setRecreatePrompt: (prompt) => set({ recreatePrompt: prompt }),
      setPendingRetryPrompt: (prompt) => set({ pendingRetryPrompt: prompt }),
      setPendingEditTab: (pending) => set({ pendingEditTab: pending }),
      setSidebarActiveTab: (tab) => set({ sidebarActiveTab: tab }),

      loadFrameFromUrl: async (url, type) => {
        try {
          // Импортируем apiClient динамически для избежания циклических зависимостей
          const { apiClient } = await import('../services/api');
          
          // Извлекаем S3 ключ из URL
          // URL формат: https://bucket.s3.region.amazonaws.com/path/to/file.ext?params
          // или https://cloudfront-domain/path/to/file.ext
          let sourceS3Key = '';
          
          if (url.includes('amazonaws.com')) {
            // Формат S3 URL
            const urlParts = url.split('.amazonaws.com/')[1];
            sourceS3Key = urlParts ? urlParts.split('?')[0] : '';
          } else if (url.includes('cloudfront.net') || url.startsWith('https://')) {
            // Формат CloudFront или custom domain
            const urlObj = new URL(url);
            sourceS3Key = urlObj.pathname.substring(1); // Убираем первый слэш
          }
          
          if (!sourceS3Key) {
            throw new Error('Could not extract S3 key from URL');
          }
          
          console.log(`📋 Extracted S3 key: ${sourceS3Key}`);
          
          // Определяем тип файла по расширению
          const fileExtension = sourceS3Key.split('.').pop()?.toLowerCase() || '';
          const isVideoFile = ['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(fileExtension);
          const mimeType = isVideoFile 
            ? `video/${fileExtension === 'mov' ? 'quicktime' : fileExtension}`
            : 'image/jpeg';
          const fileType = isVideoFile ? 'video' : 'image';
          const fileName = `${type}-frame.${fileExtension || 'jpg'}`;
          
          // Проверяем, есть ли уже кадр этого типа для удаления
          // Сохраняем ссылку на старый кадр перед заменой
          let frameToDelete: VideoFrame | undefined;
          
          // Создаем временный frame с индикатором загрузки
          const frameId = `${type}-recreate-${Date.now()}`;
          const placeholderFile = new File([''], fileName, { type: mimeType });
          
          const tempFrame: VideoFrame = {
            id: frameId,
            file: placeholderFile,
            dataUrl: url, // Временно используем оригинальный URL
            s3Key: `temp-${frameId}`,
            type,
            name: fileName,
            isUploading: true,
            uploadProgress: 50,
            fromGallery: false, // Это КОПИЯ - должна удаляться из S3 при удалении из composer
            originalS3Key: sourceS3Key, // Сохраняем ссылку на оригинал для метаданных
            isVideo: type === 'referenceVideo' || isVideoFile // Для referenceVideo всегда true
          };
          
          // Добавляем временный frame (заменяем существующий)
          set((state) => {
            const existingIndex = state.frames.findIndex((f: VideoFrame) => f.type === type);
            if (existingIndex !== -1) {
              // Сохраняем старый кадр для последующего удаления
              frameToDelete = state.frames[existingIndex];
              
              const newFrames = [...state.frames];
              newFrames[existingIndex] = tempFrame;
              return { frames: newFrames };
            }
            return { frames: [...state.frames, tempFrame] };
          });
          
          // Создаем КОПИЮ в S3 через бэкенд (без загрузки в браузер)
          const copyResult = await apiClient.copyFileInS3(
            sourceS3Key,
            'user-uploads/video-frames-copies'
          );
          
          if (!copyResult.success || !copyResult.data) {
            throw new Error('Failed to copy file in S3');
          }
          
          const { s3Key, publicUrl } = copyResult.data;
          
          // Сохраняем в БД для синхронизации между устройствами и сохранения при перезагрузке
          let dbId: string | undefined;
          // Маппинг типа в position: 0=start, 1=end, 2=reference, 3=actTwoCharacter, 4=referenceVideo
          const positionMap: Record<string, number> = {
            'start': 0,
            'end': 1,
            'reference': 2,
            'actTwoCharacter': 3,
            'referenceVideo': 4
          };
          try {
            const { currentProvider, currentModel } = get();
            // Только специфичные типы (reference, actTwoCharacter, referenceVideo) привязываем к модели
            // Start/End кадры (position 0, 1) - общие для всех моделей
            const position = positionMap[type] ?? 0;
            const isModelSpecific = position >= 2; // 2=reference, 3=actTwoCharacter, 4=referenceVideo
            const result = await apiClient.saveVideoComposerAttachment({
              fileName,
              fileType,
              mimeType,
              fileSize: 0, // Размер неизвестен для скопированного файла
              s3Key,
              s3Url: publicUrl,
              fromGallery: false, // Это КОПИЯ - должна удаляться
              position,
              ...(isModelSpecific ? { provider: currentProvider, model: currentModel } : {})
            });
            dbId = result.attachment?.id;
          } catch (dbError) {
            // Продолжаем даже если не удалось сохранить в БД
          }
          
          // Обновляем frame с реальными данными
          const finalFrame: VideoFrame = {
            ...tempFrame,
            dataUrl: publicUrl, // URL копии
            s3Key, // Новый S3 ключ копии
            isUploading: false,
            uploadProgress: 100,
            dbId
          };
          
          set((state) => {
            const updatedFrames = state.frames.map(f =>
              f.id === frameId ? finalFrame : f
            );
            return { frames: updatedFrames };
          });
          
          // Удаляем старый кадр из S3 и БД (если был)
          if (frameToDelete) {
            // Удаляем из БД если есть dbId
            if (frameToDelete.dbId) {
              try {
                await apiClient.deleteVideoComposerAttachment(frameToDelete.dbId);
              } catch (error) {
                // Failed to delete old frame from DB
              }
            }
            
            // Удаляем из S3 только если это НЕ из галереи (не удаляем оригиналы)
            const shouldDeleteFromS3 = frameToDelete.s3Key && 
              !frameToDelete.s3Key.startsWith('temp-') && 
              !frameToDelete.fromGallery;
            
            if (shouldDeleteFromS3) {
              try {
                await apiClient.deleteFromS3([frameToDelete.s3Key]);
              } catch (error) {
                // Failed to delete old frame from S3
              }
            }
          }
        } catch (error) {
          throw error;
        }
      },
      
      loadReferenceImageFromUrl: async (url, s3Key) => {
        try {
          // Импортируем apiClient динамически для избежания циклических зависимостей
          const { apiClient } = await import('../services/api');
          
          // Если s3Key не передан, пытаемся извлечь из URL
          let sourceS3Key = s3Key || '';
          
          if (!sourceS3Key) {
            if (url.includes('amazonaws.com')) {
              // Формат S3 URL
              const urlParts = url.split('.amazonaws.com/')[1];
              sourceS3Key = urlParts ? urlParts.split('?')[0] : '';
            } else if (url.includes('cloudfront.net') || url.startsWith('https://')) {
              // Формат CloudFront или custom domain
              const urlObj = new URL(url);
              sourceS3Key = urlObj.pathname.substring(1); // Убираем первый слэш
            }
          }
          
          if (!sourceS3Key) {
            throw new Error('Could not extract S3 key from URL');
          }
          
          // Определяем тип файла по расширению
          const fileExtension = sourceS3Key.split('.').pop()?.toLowerCase() || 'jpg';
          const mimeType = fileExtension === 'png' ? 'image/png' : 'image/jpeg';
          const fileName = `reference-image-${Date.now()}.${fileExtension}`;
          
          // Создаем временный reference image с индикатором загрузки
          const imageId = `ref-recreate-${Date.now()}-${Math.random()}`;
          const placeholderFile = new File([''], fileName, { type: mimeType });
          
          const tempImage: VideoFrame = {
            id: imageId,
            file: placeholderFile,
            dataUrl: url, // Временно используем оригинальный URL
            s3Key: `temp-${imageId}`,
            type: 'reference',
            name: fileName,
            isUploading: true,
            uploadProgress: 50,
            fromGallery: false, // Это КОПИЯ - должна удаляться из S3 при удалении из composer
            originalS3Key: sourceS3Key // Сохраняем ссылку на оригинал для метаданных
          };
          
          // Добавляем временный reference image
          set((state) => {
            return { referenceImages: [...state.referenceImages, tempImage] };
          });
          
          // Создаем КОПИЮ в S3 через бэкенд (без загрузки в браузер)
          const copyResult = await apiClient.copyFileInS3(
            sourceS3Key,
            'user-uploads/video-reference-images-copies'
          );
          
          if (!copyResult.success || !copyResult.data) {
            throw new Error('Failed to copy file in S3');
          }
          
          const { s3Key: newS3Key, publicUrl } = copyResult.data;
          
          // Сохраняем в БД для синхронизации между устройствами и сохранения при перезагрузке
          let dbId: string | undefined;
          try {
            const { currentProvider, currentModel } = get();
            const result = await apiClient.saveVideoComposerAttachment({
              fileName,
              fileType: 'image',
              mimeType,
              fileSize: 0, // Размер неизвестен для скопированного файла
              s3Key: newS3Key,
              s3Url: publicUrl,
              fromGallery: false, // Это КОПИЯ - должна удаляться
              position: 2, // 2=reference для восстановления типа
              provider: currentProvider,
              model: currentModel
            });
            dbId = result.attachment?.id;
          } catch (dbError) {
            // Продолжаем даже если не удалось сохранить в БД
          }
          
          // Обновляем reference image с реальными данными
          const finalImage: VideoFrame = {
            ...tempImage,
            dataUrl: publicUrl, // URL копии
            s3Key: newS3Key, // Новый S3 ключ копии
            isUploading: false,
            uploadProgress: 100,
            dbId
          };
          
          set((state) => {
            const updatedReferenceImages = state.referenceImages.map(img =>
              img.id === imageId ? finalImage : img
            );
            return { referenceImages: updatedReferenceImages };
          });
        } catch (error) {
          throw error;
        }
      },
      
      loadInputVideoFromUrl: async (url, duration) => {
        try {
          // Импортируем apiClient динамически для избежания циклических зависимостей
          const { apiClient } = await import('../services/api');
          
          // Извлекаем S3 ключ из URL
          let sourceS3Key = '';
          
          if (url.includes('amazonaws.com')) {
            const urlParts = url.split('.amazonaws.com/')[1];
            sourceS3Key = urlParts ? urlParts.split('?')[0] : '';
          } else if (url.includes('cloudfront.net') || url.startsWith('https://')) {
            const urlObj = new URL(url);
            sourceS3Key = urlObj.pathname.substring(1);
          }
          
          if (!sourceS3Key) {
            throw new Error('Could not extract S3 key from URL');
          }
          
          // Создаем временный input video с URL
          const videoId = `input-video-recreate-${Date.now()}-${Math.random()}`;
          const fileName = `input-video-${Date.now()}.mp4`;
          const mimeType = 'video/mp4';
          const placeholderFile = new File([''], fileName, { type: mimeType });
          
          // Сначала ставим с оригинальным URL, затем копируем
          const tempVideo: VideoFrame = {
            id: videoId,
            file: placeholderFile,
            dataUrl: url,
            s3Key: sourceS3Key,
            type: 'inputVideo',
            name: fileName,
            isUploading: true,
            uploadProgress: 0,
            isVideo: true,
            videoDuration: duration || 5
          };
          
          set({ 
            inputVideo: tempVideo,
            inputVideoDuration: duration || 5
          });
          
          // Копируем файл в папку текущего пользователя
          const copyResult = await apiClient.copyFileInS3(
            sourceS3Key,
            'user-uploads/video-input-video-copies'
          );
          
          if (!copyResult.success || !copyResult.data) {
            throw new Error(copyResult.error || 'Failed to copy input video');
          }
          
          const { s3Key: newS3Key, publicUrl } = copyResult.data;
          
          // Сохраняем в БД
          let dbId: string | undefined;
          try {
            const { currentProvider, currentModel } = get();
            const result = await apiClient.saveVideoComposerAttachment({
              fileName,
              fileType: 'video',
              mimeType,
              s3Key: newS3Key,
              s3Url: publicUrl,
              fromGallery: false,
              position: 5, // 5=inputVideo
              provider: currentProvider,
              model: currentModel
            });
            dbId = result.attachment?.id;
          } catch (dbError) {
            // Продолжаем даже если не удалось сохранить в БД
          }
          
          // Обновляем input video с реальными данными
          const finalVideo: VideoFrame = {
            ...tempVideo,
            dataUrl: publicUrl,
            s3Key: newS3Key,
            isUploading: false,
            uploadProgress: 100,
            dbId
          };
          
          set({ 
            inputVideo: finalVideo,
            inputVideoDuration: duration || 5
          });
        } catch (error) {
          // Очищаем при ошибке
          set({ inputVideo: null, inputVideoDuration: null });
          throw error;
        }
      },
      
      // Team mode: тригер для принудительного обновления при SSE событиях
      triggerTeamRefresh: () =>
        set((state) => ({
          teamRefreshTrigger: state.teamRefreshTrigger + 1
        })),
    }),
    {
      name: 'video-store',
      storage: createConsentAwareJSONStorage(), // .ru: гейтится functional consent (FZ-152)
      partialize: (state) => ({
        // videos НЕ сохраняем - будут загружаться свежие с сервера
        // isGenerating, generatingCount, activeGenerations, activeGenerationDetails НЕ сохраняем - это runtime состояние
        // frames и referenceImages НЕ сохраняем - содержат File объекты, которые не сериализуются в JSON
        // и вызывают проблемы производительности при частом переключении вкладок
        currentProvider: state.currentProvider,
        currentModel: state.currentModel,
        ratio: state.ratio,
        quality: state.quality,
        duration: state.duration,
        modelSettings: state.modelSettings,
        currentPrompt: state.currentPrompt,
        autoTranslate: state.autoTranslate,
        favoriteMotionIds: state.favoriteMotionIds,
        generationMode: state.generationMode,
        audioEnabled: state.audioEnabled, // Сохраняем состояние аудио для Veo
        multiShotEnabled: state.multiShotEnabled, // Multi-Shot для Kling 3.0
        shots: state.shots, // Multi-Shot scenes
        selectedElements: state.selectedElements, // Выбранные элементы (Kling)
        sidebarActiveTab: state.sidebarActiveTab, // Активная вкладка sidebar
        // НЕ сохраняем: frames, referenceImages - они загружаются из БД при открытии страницы
        // referenceVideos/Audios НЕ сохраняем — загружаются из БД через useVideoComposerSync
      }),
    }
  )
);

// SWR Cache helper functions (вне store для избежания проблем с типизацией)
const isCacheValidVideo = (key: string): boolean => {
  const state = useVideoStore.getState();
  if (state.cacheKey !== key) return false;
  if (!state.lastLoadedAt) return false;
  const cacheAge = Date.now() - state.lastLoadedAt;
  return cacheAge < 30000; // 30 секунд
};

const hasCachedDataVideo = (key: string): boolean => {
  const state = useVideoStore.getState();
  return state.cacheKey === key && state.videos.length > 0;
};

export { useVideoStore, isCacheValidVideo, hasCachedDataVideo };

